import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import * as qs from 'qs';
import { environment } from '../../../../../environments/environment';
import { PaginacaoResponse } from '../../../../models/paginacaoResponse.interface';
import { UserService } from '../../../../shared/services/user/user.service';
import {
  cursoRequest,
  cursoResponse,
  materiaRequest,
  materiaResponse,
  tipoAvaliacao,
} from './../../../../models/cursos.interface';
import {
  listarPaginacao,
  Post,
  SingleResponse,
} from './../../../../models/rest.interface';

const URL_BASE = environment.BACKEND_API;
@Injectable({
  providedIn: 'root',
})
export class CursosService {

  private cursoSubject: BehaviorSubject<any> = new BehaviorSubject(null);

  idEscola!: number;
  constructor(
    private readonly http: HttpClient,
    private readonly user: UserService,
  ) {
    this.user.escola.subscribe((value) => {
      if (value !== undefined) {
        this.idEscola = value.id;
      }
    });
  }

  get getCurso(): any {
    return this.cursoSubject.getValue();
  }

  setCurso(curso: any): void {
    this.cursoSubject.next(curso);
  }

  buscarCursos(find: string): Observable<any> {
    return this.http.get<any>(
      `${URL_BASE}/cursos/?filters[$and][0][escola][$eq]=${this.idEscola}&filters[$and][1][nomeCurso][$containsi]=${find}`,
    );
  }

  salvarCurso(curso: Post<cursoRequest>): Observable<any> {
    return this.http.post<any>(`${URL_BASE}/cursos`, curso);
  }

  salvarTipoAvaliacao(tipoAvaliacao: Post<any>): Observable<any> {
    return this.http.post<any>(`${URL_BASE}/tipo-avaliacaos`, tipoAvaliacao);
  }

  verificarTipoAvaliacao(documentId: string): Observable<any> {
    return this.http.get<any>(`${URL_BASE}/tipo-avaliacaos/${documentId}`);
  }

  atualizarTipoAvaliacao(
    documentId: string,
    request: Post<any>,
  ): Observable<any> {
    return this.http.put<any>(
      `${URL_BASE}/tipo-avaliacaos/${documentId}`,
      request,
    );
  }

  listarCursosPorEscola(
    params: listarPaginacao = {},
  ): Observable<PaginacaoResponse<cursoResponse>> {
    let httpParams = new HttpParams()
      // .set('sort', params.sort || 'descending')
      .set('pagination[withCount]', 'true')
      .set('pagination[page]', (params.page ?? 1).toString())
      .set('pagination[pageSize]', (params.pageSize ?? 10).toString());
    return this.http.get<PaginacaoResponse<cursoResponse>>(
      `${URL_BASE}/cursos/?filters[escola][$eq]=${this.idEscola}&filters[ativo]=true`,
      { params: httpParams },
    );
  }

  listarMateriasPorEscola(
    params: listarPaginacao = {},
  ): Observable<PaginacaoResponse<materiaResponse>> {
    let httpParams = new HttpParams()
      // .set('sort', params.sort || 'descending')
      .set('pagination[withCount]', 'true')
      .set('pagination[page]', (params.page ?? 1).toString())
      .set('pagination[pageSize]', (params.pageSize ?? 10).toString());
    return this.http.get<PaginacaoResponse<materiaResponse>>(
      `${URL_BASE}/materias/?filters[escolas][$eq]=${this.idEscola}`,
      { params: httpParams },
    );
  }

  buscarCursoPorId(documentId: any): Observable<SingleResponse<cursoResponse>> {
    return this.http.get<SingleResponse<cursoResponse>>(
      `${URL_BASE}/cursos/${documentId}?populate=*`,
    );
  }

  buscarCursoEDITAR(
    documentId: any,
  ): Observable<SingleResponse<cursoResponse>> {
    const query = qs.stringify({
      populate: {
        materias: true,
        turmas: true,
        tipo_avaliacaos: {
          populate: {
            notas: true,
          },
        },
      },
    });

    return this.http.get<SingleResponse<cursoResponse>>(
      `${URL_BASE}/cursos/${documentId}?${query}`,
    );
  }

  buscarCurso(idCurso: any): Observable<SingleResponse<cursoResponse>> {
    return this.http.get<SingleResponse<cursoResponse>>(
      `${URL_BASE}/cursos/${idCurso}/?filters[escolas][$eq]=${this.idEscola}&populate=*`,
    );
  }

  editarCurso(
    cursoRequest: Post<any>,
    cursoId: string,
  ): Observable<SingleResponse<cursoResponse>> {
    return this.http.put<SingleResponse<cursoResponse>>(
      `${URL_BASE}/cursos/${cursoId}`,
      cursoRequest,
    );
  }

  deletarCurso(idCurso: string, cursoRequest: any): Observable<any> {
    return this.http.put<any>(`${URL_BASE}/cursos/${idCurso}`, cursoRequest);
  }

  buscarMateriasPorCurso(
    idCurso: number,
  ): Observable<SingleResponse<materiaResponse>> {
    return this.http.get<SingleResponse<materiaResponse>>(
      `${URL_BASE}/materias/?filters[cursos][$eq]=${idCurso}&populate=*`,
    );
  }

  buscarMateriasPorCursoPopulateNotas(
    idAluno: number,
    periodo: string,
    idTurma: number,
  ): Observable<any> {
    const query = qs.stringify(
      {
        filters: {
          $and: [
            {
              aluno: {
                $eq: idAluno,
              },
            },
            {
              turma: {
                $eq: idTurma,
              },
            },
            {
              periodo: {
                $eq: periodo,
              },
            },
          ],
        },
        populate: {
          materia: true,
        },
      },
      {
        encodeValuesOnly: true,
      },
    );

    return this.http.get<any>(`${URL_BASE}/notas/?${query}`);
  }

  buscarMateriasPorCursoEPeriodoEAlunoAPP(
    idCurso: number,
    periodo: string,
    idAluno: number,
    idTurma: number,
  ): Observable<SingleResponse<materiaResponse>> {
    const query = qs.stringify(
      {
        filters: {
          cursos: {
            $eq: idCurso,
          },
        },
        populate: {
          grade_curriculars: {
            filters: {
              periodo: {
                $eq: periodo,
              },
              frequencias: {
                alunos: {
                  $eq: idAluno,
                },
              },
              turma: {
                $eq: idTurma,
              },
            },
            populate: {
              frequencias: {
                filters: {
                  alunos: {
                    $eq: idAluno,
                  },
                },
                populate: ['alunos'],
              },
            },
          },
        },
      },
      {
        encodeValuesOnly: true,
      },
    );

    return this.http.get<SingleResponse<materiaResponse>>(
      `${URL_BASE}/materias?${query}`,
    );
  }

  buscarMateriasPorCursoEAlunoAPP(
    idCurso: number,
    idAluno: number,
  ): Observable<SingleResponse<materiaResponse>> {
    return this.http.get<SingleResponse<materiaResponse>>(
      `${URL_BASE}/materias/?filters[cursos][$eq]=${idCurso}&populate[frequencias][filters][alunos][$eq]=${idAluno}&populate[frequencias][populate][0]=alunos`,
    );
  }

  buscarMateriasPorUsuario(
    idUser: number,
  ): Observable<SingleResponse<materiaResponse>> {
    return this.http.get<SingleResponse<materiaResponse>>(
      `${URL_BASE}/materias/?filters[users][$eq]=${idUser}`,
    );
  }
}

export interface curso {
  label: any;
  idCurso: any;
  documentId: any;
}
